import { FormattedMessage, IntlShape } from 'react-intl';
import { captureMessage } from '@tink/core/src/sentry';
import { BankId } from '@tink/core/src/components/atoms/Icons/BankId';
import { Bank } from '@tink/core/src/components/atoms/Icons/Bank';
import { PersonalCode } from '@tink/core/src/components/atoms/Icons/PersonalCode';
import { Spacer } from '@tink/core/src/components/atoms/Spacer/Spacer';
import { useIntl } from '@tink/core/src/components/providers/LanguageProvider/useIntl';
import { CircledIcon } from '../../../components/atoms/Icons/CircledIcon';
import { Header } from '../../../components/organisms/Header/Header';
import { Provider, ProviderEntity } from '../../../models/provider';
import { H1 } from '../../../components/atoms/Heading/Heading';
import { ContentBody } from '../../../components/atoms/ContentBody/ContentBody';
import { ScreenWrapper } from '../../../components/atoms/ScreenWrapper/ScreenWrapper';
import { CredentialsType } from '../../../models/credentials';
import { ProviderViewSet } from '../../../utilities/provider-view-set';
import { SelectableOption } from '../../../components/screens/molecules/SelectableOption/SelectableOption';
import { Subtitle } from '../../../components/atoms/Subtitle/Subtitle';
import { PageView } from '../../../models/analytics';

import { useVersion } from '../../../components/ApplicationProviders/VersionProvider/VersionProvider';
import { usePageViewEvents } from '../../Analytics/usePageViewEvents';
import { messages } from './messages';

export const providerSignInMethodIcon = (provider: ProviderEntity) => {
  if (Provider.isThirdPartyAppType(provider)) {
    return <CircledIcon Icon={Bank} />;
  }

  if (Provider.isMobileBankId(provider)) {
    return <CircledIcon Icon={BankId} />;
  }

  if (Provider.isPassword(provider)) {
    return <CircledIcon Icon={PersonalCode} />;
  }

  return undefined;
};

export const providerSignInMethodDescription = (intl: IntlShape, provider: ProviderEntity) => {
  if (provider.displayDescription) {
    return provider.displayDescription;
  }

  switch (provider.credentialsType) {
    case CredentialsType.PASSWORD:
      return intl.formatMessage(messages.credentialsTypePassword);
    case CredentialsType.MOBILE_BANKID:
      return intl.formatMessage(messages.credentialsTypeMobileBankId);
    case CredentialsType.KEYFOB:
      return intl.formatMessage(messages.credentialsTypeKeyfob);
    case CredentialsType.THIRD_PARTY_APP:
      return intl.formatMessage(messages.credentialsTypeThirdpartyApp);
    default:
      captureMessage('Unknown credentials type', {
        custom: {
          credentialsType: provider.credentialsType,
        },
      });

      return '';
  }
};

type OnProviderItemClick = ({ credentialsType }: { credentialsType: CredentialsType }) => void;

export type CredentialsTypePickerProps = {
  onBack: () => void;
  providers: ProviderEntity[];
  onProviderItemClick: OnProviderItemClick;
};

export const CredentialsTypePicker = ({
  providers,
  onBack,
  onProviderItemClick,
}: CredentialsTypePickerProps) => {
  const intl = useIntl();
  const {
    version: { version },
  } = useVersion();

  const providerViewSet = new ProviderViewSet(providers, version).groupByCredentialsType();

  usePageViewEvents({
    pageView: PageView.CREDENTIALS_TYPE_SELECTION_SCREEN,
  });

  return (
    <ScreenWrapper data-testid="CredentialsTypePicker">
      <Header onBack={onBack} pageView={PageView.CREDENTIALS_TYPE_SELECTION_SCREEN} />
      <ContentBody>
        <H1>
          <FormattedMessage {...messages.signInMethod} />
        </H1>
        <Subtitle>
          <FormattedMessage {...messages.chooseConnectionMethod} />
        </Subtitle>
        <Spacer size="normal" />
        {providerViewSet.map((viewSet) => {
          const provider = viewSet.providers[0];

          return (
            <SelectableOption
              key={provider.name}
              Icon={providerSignInMethodIcon(provider)}
              text={providerSignInMethodDescription(intl, provider)}
              onClick={() => {
                onProviderItemClick({ credentialsType: provider.credentialsType });
              }}
            />
          );
        })}
      </ContentBody>
    </ScreenWrapper>
  );
};
